import { Gauge, GaugeConfig } from '@ant-design/plots';
import useSwr from '@hooks/useSwr';
import SessionContext from '@providers/Session/SessionContext';
import { Col, Empty, Row, Select, Spin, Typography } from 'antd';
import React, {
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Filters, FiltersCard } from '@components/Filters';
import { IObject } from '@utils/defaultTypes';
import { CreateAlarmButton } from '@components/CreateAlarmButton/CreateAlarmButton';
import { CreateAlarmSteps } from '@components/Insights/CreateAlarm/CreateAlarmSteps';
import rolesConstants from '@utils/rolesConstants';
import RoleChecker from '@components/RoleChecker';
import { TransactionsPayGraphTypes } from '@utils/alarmsConstants';
import * as S from './styles';
import { IOption } from '..';

interface ITags {
  appname: string;
}
interface ISeries {
  values: ICharValues;
  tags: ITags;
}
type ICharValues = [timestamp: number, total: number | null][];
interface ICharDataResponse {
  results?: {
    series?: ISeries[];
  }[];
}
type GroupedItem = {
  appname: string;
  data: number;
};
interface IStyleCss {
  [key: string]: CSSProperties;
}
const MIN_VALUE = 0;
const DEFAULT_FORM_VALUE = {
  groupByTime: '5m',
  lastTime: '10m',
};
const TOTAL_VALUE_CHART = 5000;
const COLORS = {
  GREEN: 'rgb(42, 255, 149)',
  LIGHT_GREEN: 'rgba(104, 205, 154, 0.3)',
  YELLOW: 'rgb(234, 255, 42)',
  LIGHT_YELLOW: 'rgba(202, 205, 104, 0.3)',
  RED: 'rgb(255, 42, 42)',
  LIGHT_RED: 'rgba(205, 104, 104, 0.3)',
};
const PERCENTAGES = {
  ZERO: 0,
  LOW: 0.6,
  MEDIUM: 0.8,
  HIGH: 1,
};
const colorMap: { [key: string]: string[] } = {
  ZERO: [COLORS.LIGHT_GREEN, COLORS.LIGHT_YELLOW, COLORS.LIGHT_RED],
  LOW: [
    COLORS.GREEN,
    COLORS.LIGHT_GREEN,
    COLORS.LIGHT_YELLOW,
    COLORS.LIGHT_RED,
  ],
  EXACT_LOW: [COLORS.GREEN, COLORS.LIGHT_YELLOW, COLORS.LIGHT_RED],
  MEDIUM: [COLORS.GREEN, COLORS.YELLOW, COLORS.LIGHT_YELLOW, COLORS.LIGHT_RED],
  EXACT_MEDIUM: [COLORS.GREEN, COLORS.YELLOW, COLORS.LIGHT_RED],
  NEAR_HIGH: [COLORS.GREEN, COLORS.YELLOW, COLORS.RED, COLORS.LIGHT_RED],
  HIGH: [COLORS.GREEN, COLORS.YELLOW, COLORS.RED],
};
const { Paragraph } = Typography;

interface IProps {
  options: {
    lastTimeOptions: IOption[];
    groupedOptions: IOption[];
  };
  setSearchParams: (values: IObject) => void;
  searchParams: IObject;
  getOptionsValue: (searchParam: string, defaultValue: string) => string;
}
const AverageTransactionTime = ({
  options,
  setSearchParams,
  searchParams,
  getOptionsValue,
}: IProps) => {
  const styles: IStyleCss = {
    formSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      marginBottom: '1rem',
    },
    select: {
      width: '100%',
      maxWidth: '15rem',
    },
    spinner: {
      width: '100%',
      padding: '5rem 1rem',
      margin: 16,
    },
    formTitle: { margin: 0 },
    charTitle: {
      margin: '0.5rem 0',
      width: '100%',
      textAlign: 'center',
      fontWeight: '600',
      fontSize: '1rem',
    },
    notFoundMessage: {
      margin: '1rem auto 0',
      textAlign: 'center',
    },
  };

  const [formData, setFormData] = useState({
    groupByTime: getOptionsValue(
      searchParams?.groupByTimeAverage,
      DEFAULT_FORM_VALUE?.groupByTime,
    ),
    lastTime: getOptionsValue(
      searchParams?.lastTimeAverage,
      DEFAULT_FORM_VALUE?.lastTime,
    ),
  });
  const { customersSwitch, selectedApp, theme } = useContext(SessionContext);
  const [openCreateAlarmModal, setOpenCreateAlarmModal] = useState(false);
  const [selectedAppName, setSelectedAppName] = useState<string>('');
  const [data, setData] = useState<GroupedItem[]>([]);
  const [showFilters, setShowFilters] = useState(true);
  const { t } = useTranslation(['average-transaction-time']);

  const fetchParams = useMemo(
    () => ({
      params: {
        produto: selectedApp?.toUpperCase(),
        lastTime: formData?.lastTime,
        groupByTime: formData?.groupByTime,
      },
      headers: {
        customerId: customersSwitch?.selected,
      },
    }),
    [customersSwitch?.selected, selectedApp, formData],
  );

  const { data: chartData, isLoading: charIsLoading } =
    useSwr<ICharDataResponse>(
      '/service-proxy/dashboard/transacao/historico/velocimetro',
      fetchParams,
    );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const groupedData = chartData?.results?.[0]?.series?.map(
          (seriesData: ISeries) => {
            const dataFiltered = seriesData?.values
              ?.filter(valuesData => !!valuesData[valuesData?.length - 1])
              ?.map(
                valuesFiltered => valuesFiltered[valuesFiltered?.length - 1],
              ) as number[];
            const averageValue = getAverageValue(dataFiltered);
            return {
              appname: seriesData?.tags?.appname,
              data: averageValue,
            };
          },
        );
        if (groupedData?.length) setData(groupedData);
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
      }
    };
    fetchData();
  }, [chartData, fetchParams, formData]);

  const getAverageValue = (listValues: number[]) => {
    const sum = listValues.reduce((acc, num) => acc + num, MIN_VALUE);
    return sum / listValues?.length;
  };

  const calculateTicks = (percentValue: number): number[] => {
    const { LOW, MEDIUM, HIGH } = PERCENTAGES;
    if (percentValue === LOW) return [LOW, MEDIUM, HIGH];
    if (percentValue < LOW) return [percentValue, LOW, MEDIUM, HIGH];
    if (percentValue === MEDIUM) return [LOW, MEDIUM, HIGH];
    if (percentValue < MEDIUM) return [LOW, percentValue, MEDIUM, HIGH];
    if (percentValue === HIGH) return [LOW, MEDIUM, HIGH];
    if (percentValue < HIGH) return [LOW, MEDIUM, percentValue, HIGH];
    return [LOW, MEDIUM, HIGH];
  };

  const calculateColors = (value: number): string[] => {
    if (value === PERCENTAGES.ZERO) return colorMap.ZERO;
    if (value < PERCENTAGES.LOW) return colorMap.LOW;
    if (value === PERCENTAGES.LOW) return colorMap.EXACT_LOW;
    if (value < PERCENTAGES.MEDIUM) return colorMap.MEDIUM;
    if (value === PERCENTAGES.MEDIUM) return colorMap.EXACT_MEDIUM;
    if (value < PERCENTAGES.HIGH) return colorMap.NEAR_HIGH;
    return colorMap.HIGH;
  };

  const getChartValueFormatted = (chartValue: number) => {
    const conversionFactor = 1000;
    const decimal = 2;
    if (chartValue < conversionFactor)
      return `${chartValue.toFixed(decimal)} ms`;
    return `${(chartValue / conversionFactor).toFixed(decimal)} s`;
  };

  const charConfig = useMemo(
    () =>
      data?.map?.(value => {
        const percent = value?.data / TOTAL_VALUE_CHART;
        return {
          appname: value?.appname,
          config: {
            percent,
            autoFit: true,
            indicator: false,
            range: {
              ticks: calculateTicks(percent),
              color: calculateColors(percent),
              width: 11,
            },
            startAngle: Math.PI,
            endAngle: 2 * Math.PI,
            axis: {
              tickLine: null,
              label: null,
            },
            statistic: {
              content: {
                formatter: () => `${getChartValueFormatted(value?.data)}`,
                style: {
                  fontSize: '1rem',
                  fill: theme?.colors?.text,
                  color: theme?.colors?.text,
                  fontWeight: 'bold',
                },
              },
            },
          } as GaugeConfig,
        };
      }),
    [data, theme],
  );
  const { isLoadingData, hasData } = useMemo(
    () => ({
      isLoadingData: charIsLoading,
      hasData: !!data?.length,
    }),
    [charIsLoading, data],
  );

  const filters = useMemo(
    () => [
      {
        visible: true,
        col: {
          xs: 24,
          sm: 12,
          md: 8,
          lg: 6,
          xl: 5,
        },
        label: t('average-transaction-time:labelPeriod'),
        element: (
          <Select
            onChange={lastTime => {
              setFormData(state => ({ ...state, lastTime }));
              setSearchParams({
                lastTimeAverage: lastTime,
              });
            }}
            value={getOptionsValue(
              searchParams?.lastTimeAverage,
              formData?.lastTime,
            )}
            options={options?.lastTimeOptions}
            style={styles.select}
          />
        ),
      },
      {
        visible: true,
        col: {
          xs: 24,
          sm: 12,
          md: 8,
          lg: 6,
          xl: 5,
        },
        label: t('average-transaction-time:labelGrouping'),
        element: (
          <Select
            onChange={groupByTime => {
              setFormData(state => ({ ...state, groupByTime }));
              setSearchParams({
                groupByTimeAverage: groupByTime,
              });
            }}
            options={options?.groupedOptions}
            value={getOptionsValue(
              searchParams?.groupByTimeAverage,
              formData?.groupByTime,
            )}
            style={styles.select}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData?.groupByTime, formData?.lastTime, options, t, searchParams],
  );

  const handleOpenFilters = useCallback(() => {
    setShowFilters(!showFilters);
  }, [showFilters]);

  return (
    <S.DashContainer>
      <Row gutter={[16, 16]}>
        <Col xl={24} xs={24}>
          <S.CardContainer
            title={
              <div className="card-header">
                <div className="card-title-box">
                  <div className="card-title">{t('title')}</div>
                  <Filters isOpen={showFilters} onOpen={handleOpenFilters} />
                </div>
              </div>
            }
          >
            {filters && (
              <S.FiltersContainer>
                <FiltersCard isOpen={showFilters} tableFilters={filters} />
              </S.FiltersContainer>
            )}
            {(() => {
              if (isLoadingData) {
                return (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '400px',
                    }}
                  >
                    <Spin style={styles.spinner} />
                  </div>
                );
              }

              if (hasData) {
                return (
                  <S.DashboardContainer>
                    <Row gutter={[16, 16]}>
                      {charConfig?.map(value => (
                        <Col
                          xs={24}
                          sm={12}
                          md={8}
                          lg={6}
                          xl={4}
                          xxl={4}
                          key={value.appname}
                        >
                          <S.DashboardCardContainer>
                            <RoleChecker
                              role={
                                rolesConstants.INSIGHTS_TRANSACTIONS_ALARM_MANAGE_CONFIGURATION
                              }
                            >
                              <CreateAlarmButton
                                tooltipPosition="leftTop"
                                style={{
                                  position: 'absolute',
                                  right: 10,
                                  top: 8,
                                  zIndex: '999',
                                }}
                                openCreateAlarmModal={() => {
                                  setSelectedAppName(value.appname);
                                  setOpenCreateAlarmModal(true);
                                }}
                              />
                            </RoleChecker>
                            <Gauge {...value?.config} />
                            <Paragraph style={styles.charTitle}>
                              {value?.appname}
                            </Paragraph>
                          </S.DashboardCardContainer>
                        </Col>
                      ))}
                    </Row>
                  </S.DashboardContainer>
                );
              }

              return (
                <Empty
                  description={t('average-transaction-time:notFoundMessage')}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              );
            })()}
          </S.CardContainer>
        </Col>
      </Row>
      <CreateAlarmSteps
        visible={openCreateAlarmModal}
        onClose={() => setOpenCreateAlarmModal(false)}
        preSelectedValues={{
          tipoGrafico: TransactionsPayGraphTypes.AVERAGE_TIME,
          averageTime: selectedAppName,
          appName: selectedAppName,
          skipToStepTwo: true,
        }}
        readOnlyFields={['tipoGrafico', 'averageTime']}
        hideSteps
      />
    </S.DashContainer>
  );
};
export default AverageTransactionTime;
